import { useState } from "react";
import { C, uid, fmt, Btn, Input, Select, Textarea, StatCard, useIsMobile } from "./ui";

// Config por tipo de zona — reutilizable para gallinas, conejos y chanchos
export const TIPOS_ZONA = {
  Gallinero: { icon: "🐔", prodIcon: "🥚", animal: "gallinas", produce: "huevos",   unidad: "huevos",   vendeProd: true,  catVenta: "Lana / Productos" },
  Conejera:  { icon: "🐰", prodIcon: "🐇", animal: "conejos",  produce: "crías",    unidad: "crías",    vendeProd: true,  catVenta: "Venta de animales" },
  Chiquero:  { icon: "🐖", prodIcon: "🐷", animal: "chanchos", produce: "lechones", unidad: "lechones", vendeProd: true,  catVenta: "Venta de animales" },
};

const estacionUY = (d = new Date()) => {
  const m = d.getMonth() + 1;
  return (m === 12 || m <= 2) ? "verano" : m <= 5 ? "otoño" : m <= 8 ? "invierno" : "primavera";
};

// Recomendaciones dinámicas por tipo de zona y estación (Uruguay)
const TIPS = {
  Gallinero: {
    verano:    "🌡️ Calor: agua fresca abundante y sombra. La postura puede bajar; sumá electrolitos y reduioná ración al mediodía.",
    otoño:     "🍂 Baja la luz natural y con ella la postura. Considerá luz artificial (14-16h) y subí calcio para cáscara firme.",
    invierno:  "❄️ Frío: más energía en la ración (maíz). Protegé del viento y cuidá la humedad de la cama para evitar enfermedades.",
    primavera: "🌸 Pico de postura. Asegurá calcio (conchilla) y proteína; es buena época para reponer ponedoras.",
  },
  Conejera: {
    verano:    "🌡️ Muy sensibles al calor: sombra, ventilación y agua fresca. Evitá montas en horas de calor extremo.",
    otoño:     "🍂 Buen momento reproductivo. Ajustá ración con más fibra (heno) y controlá parásitos.",
    invierno:  "❄️ Aumentá energía y protegé del frío/viento. Cuidá la humedad para evitar problemas respiratorios.",
    primavera: "🌸 Época ideal de reproducción. Buena alimentación a las madres lactantes; revisá nidos.",
  },
  Chiquero: {
    verano:    "🌡️ Necesitan barro/agua para refrescarse (no transpiran). Sombra obligatoria y agua a voluntad.",
    otoño:     "🍂 Buen engorde. Ración balanceada constante; aprovechá restos de cosecha si tenés.",
    invierno:  "❄️ Más energía en la dieta y cama seca abundante. Protegé del frío, sobre todo a los lechones.",
    primavera: "🌸 Época de partos frecuente. Cuidá la higiene del parto y la lactancia de las madres.",
  },
};

export function ZonasPanel({ zonas, insumos, onUpdate, onIngreso, onConsumirRacion }) {
  const isMobile = useIsMobile();
  const [show, setShow]   = useState(false);
  const [tipo, setTipo]   = useState("Gallinero");
  const [nombre, setNombre] = useState("");
  const [animales, setAnimales] = useState("");
  const [accion, setAccion] = useState(null); // { zonaId, modo }
  const [confirmar, setConfirmar] = useState(null);
  const [verHist, setVerHist] = useState(null); // id de zona con historial abierto
  const estacion = estacionUY();

  const hoy = () => new Date().toISOString().slice(0, 10);

  const crear = () => {
    if (!nombre) return;
    onUpdate([...zonas, {
      id: uid(), tipo, nombre, animales: parseInt(animales) || 0,
      produccion: [], racion: [], ventas: [], fecha: hoy(),
    }]);
    setShow(false); setNombre(""); setAnimales("");
  };
  const upd = (id, patch) => onUpdate(zonas.map(z => z.id === id ? { ...z, ...patch } : z));
  const eliminar = (id) => onUpdate(zonas.filter(z => z.id !== id));

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 880, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          Zonas productivas
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
          Gallinero, conejera y chiquero. Registrá producción, raciones (sale del stock) y ventas (van a Economía).
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <Btn variant="primary" onClick={() => setShow(v => !v)}>{show ? "Cancelar" : "+ Nueva zona"}</Btn>
        </div>

        {show && (
          <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.arenaDark}` }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "0 12px" }}>
              <Select label="Tipo" value={tipo} onChange={setTipo} options={Object.keys(TIPOS_ZONA)} />
              <Input label="Nombre" value={nombre} onChange={setNombre} placeholder="Ej: Gallinero del fondo" />
              <Input label={`Nº de ${TIPOS_ZONA[tipo].animal}`} value={animales} onChange={setAnimales} type="number" placeholder="0" />
            </div>
            <Btn variant="primary" onClick={crear} disabled={!nombre} style={{ width: "100%" }}>Crear zona</Btn>
          </div>
        )}

        {zonas.length === 0 && !show && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🐔</div>
            <div style={{ fontSize: 13 }}>Creá un gallinero, conejera o chiquero para llevar su producción.</div>
          </div>
        )}

        {zonas.map((z) => {
          const cfg = TIPOS_ZONA[z.tipo] || TIPOS_ZONA.Gallinero;
          const totalProd = (z.produccion || []).reduce((s, p) => s + (p.cant || 0), 0);
          const totalRacion = (z.racion || []).reduce((s, r) => s + (r.kg || 0), 0);
          const totalVentas = (z.ventas || []).reduce((s, v) => s + (v.monto || 0), 0);
          const abierto = accion?.zonaId === z.id;
          return (
            <div key={z.id} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: `1px solid ${C.arenaDark}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 30 }}>{cfg.icon}</span>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.tierra }}>{z.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {z.tipo} · <b>{z.animales}</b> {cfg.animal} · {totalProd} {cfg.unidad} acumulados
                  </div>
                </div>
                {confirmar === z.id ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => { eliminar(z.id); setConfirmar(null); }} style={{ ...btnZ, background: C.danger, color: "#fff", borderColor: C.danger }}>Sí, borrar</button>
                    <button onClick={() => setConfirmar(null)} style={btnZ}>No</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmar(z.id)} style={{ ...btnZ, color: C.danger, borderColor: "#E0909055" }}>✕</button>
                )}
              </div>

              {/* Mini-stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8, margin: "12px 0" }}>
                <StatCard val={z.animales} label={cfg.animal} />
                <StatCard val={totalProd} label={cfg.unidad} color={C.pasto} />
                <StatCard val={`${totalRacion} kg`} label="ración usada" />
                <StatCard val={`$${totalVentas.toLocaleString("es-UY")}`} label="ventas" color={C.tierra} />
              </div>

              {/* Recomendación dinámica según la época */}
              <div style={{ background:"#FBF6EA", border:"1px solid #E8D9B0", borderRadius:9,
                padding:"9px 12px", margin:"0 0 12px", fontSize:12, color:"#6B5618", lineHeight:1.5 }}>
                <b style={{ textTransform:"capitalize" }}>💡 {estacion}:</b> {(TIPS[z.tipo]||{})[estacion]}
              </div>

              {/* Acciones */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "prod" ? null : { zonaId: z.id, modo: "prod" })}>
                  {cfg.prodIcon} Producción
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "racion" ? null : { zonaId: z.id, modo: "racion" })}>
                  🌾 Ración
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "venta" ? null : { zonaId: z.id, modo: "venta" })}>
                  💰 Venta
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => setVerHist(verHist === z.id ? null : z.id)}>
                  {verHist === z.id ? "▾ historial" : "▸ historial"}
                </Btn>
              </div>

              {abierto && (
                <AccionForm zona={z} cfg={cfg} modo={accion.modo} insumos={insumos}
                  onClose={() => setAccion(null)} upd={upd} onIngreso={onIngreso} onConsumirRacion={onConsumirRacion} />
              )}

              {verHist === z.id && <Historial zona={z} cfg={cfg} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccionForm({ zona, cfg, modo, insumos, onClose, upd, onIngreso, onConsumirRacion }) {
  const hoy = () => new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy());
  const [cant, setCant]   = useState("");
  const [monto, setMonto] = useState("");
  const [queVende, setQueVende] = useState(cfg.vendeProd ? "produccion" : "animales");

  const racionesStock = insumos.filter(i => i.categoria === "Sal/Ración");
  const [racionId, setRacionId] = useState(racionesStock[0]?.id || "");
  const racionSel = racionesStock.find(i => i.id === racionId);
  const racionUnit = racionSel?.costoUnit || 0;
  const excedeRacion = modo === "racion" && racionSel && (parseFloat(cant) || 0) > (racionSel.cantidad || 0);

  const guardar = () => {
    const c = parseFloat(cant) || 0;
    if (modo === "prod") {
      if (c <= 0) return;
      upd(zona.id, { produccion: [...(zona.produccion || []), { fecha, cant: c }] });
    } else if (modo === "racion") {
      if (c <= 0 || excedeRacion) return;
      const costo = Math.round(c * racionUnit);
      const nombreRacion = racionSel?.nombre || "ración";
      upd(zona.id, { racion: [...(zona.racion || []), { fecha, kg: c, costo, nombre: nombreRacion }] });
      if (onConsumirRacion && racionId) onConsumirRacion(racionId, c, zona.nombre, fecha);
    } else if (modo === "venta") {
      const m = parseFloat(monto) || 0;
      if (c <= 0 || m <= 0) return;
      const detalle = queVende === "produccion" ? cfg.produce : cfg.animal;
      upd(zona.id, {
        ventas: [...(zona.ventas || []), { fecha, cant: c, monto: m, que: detalle }],
        // si vende animales, descontarlos
        ...(queVende === "animales" ? { animales: Math.max(0, (zona.animales || 0) - c) } : {}),
      });
      if (onIngreso) onIngreso({
        id: uid(), tipo: "ingreso", categoria: cfg.catVenta,
        concepto: `Venta ${detalle} (${c}) · ${zona.nombre}`, monto: m, fecha, obs: "Generado desde Zonas",
      });
    }
    onClose();
  };

  return (
    <div style={{ background: C.fondo, borderRadius: 10, padding: 14, marginTop: 12, border: `1px solid ${C.arenaDark}` }}>
      {modo === "prod" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
          <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
          <Input label={`Cantidad de ${cfg.unidad}`} value={cant} onChange={setCant} type="number" placeholder="Ej: 24" />
        </div>
      )}
      {modo === "racion" && (
        <>
          {racionesStock.length > 0 ? (
            <Select label="¿Qué ración usás? (sale del stock)" value={racionId} onChange={setRacionId}
              options={racionesStock.map(i => ({ v: i.id, l: `${i.nombre} — ${i.cantidad} ${i.unidad} en stock` }))} />
          ) : (
            <div style={{ fontSize: 11, color: C.danger, marginBottom: 10, lineHeight: 1.5 }}>
              ⚠ No tenés ración en el stock. Andá a <b>Insumos → + Agregar insumo</b>, categoría <b>"Sal/Ración"</b>
              (ej: "Ración ponedoras"), cargá la cantidad y el costo. Después volvé acá.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
            <Input label="Ración usada (kg)" value={cant} onChange={setCant} type="number" placeholder="Ej: 10" />
          </div>
          {racionSel && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              Disponible: <b>{racionSel.cantidad} {racionSel.unidad}</b> · ${(racionUnit).toLocaleString("es-UY", { maximumFractionDigits: 2 })}/kg
              {cant ? ` · total $${Math.round((parseFloat(cant)||0)*racionUnit).toLocaleString("es-UY")}` : ""}
            </div>
          )}
          {excedeRacion && (
            <div style={{ fontSize: 11.5, color: C.danger, fontWeight: 600, marginBottom: 8 }}>
              ⚠ No podés usar más de lo que tenés ({racionSel.cantidad} {racionSel.unidad} en stock).
            </div>
          )}
        </>
      )}
      {modo === "venta" && (
        <>
          <Select label="¿Qué vendés?" value={queVende} onChange={setQueVende}
            options={[{ v: "produccion", l: `Producción (${cfg.produce})` }, { v: "animales", l: cfg.animal }]} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
            <Input label="Cantidad" value={cant} onChange={setCant} type="number" placeholder="0" />
            <Input label="Monto total ($)" value={monto} onChange={setMonto} type="number" placeholder="0" />
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn size="sm" variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</Btn>
        <Btn size="sm" variant="primary" onClick={guardar} disabled={excedeRacion} style={{ flex: 1 }}>Guardar</Btn>
      </div>
    </div>
  );
}

// Historial unificado: producción + ración + ventas, ordenado por fecha
function Historial({ zona, cfg }) {
  const eventos = [
    ...(zona.produccion || []).map(p => ({ fecha: p.fecha, ic: cfg.prodIcon, txt: `Producción`, val: `+${p.cant} ${cfg.unidad}`, col: C.pasto })),
    ...(zona.racion || []).map(r => ({ fecha: r.fecha, ic: "🌾", txt: `Ración${r.nombre ? ` (${r.nombre})` : ""}`, val: `−${r.kg} kg${r.costo ? ` · $${r.costo.toLocaleString("es-UY")}` : ""}`, col: C.danger })),
    ...(zona.ventas || []).map(v => ({ fecha: v.fecha, ic: "💰", txt: `Venta ${v.que} (${v.cant})`, val: `+$${(v.monto || 0).toLocaleString("es-UY")}`, col: C.tierra })),
  ].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${C.arena}`, paddingTop: 10 }}>
      {eventos.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Sin movimientos todavía.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
        {eventos.map((e, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12,
            background: C.fondo, borderRadius: 6, padding: "6px 10px" }}>
            <span style={{ fontSize: 15 }}>{e.ic}</span>
            <span style={{ color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmt(e.fecha)}</span>
            <span style={{ flex: 1 }}>{e.txt}</span>
            <span style={{ fontWeight: 700, color: e.col, whiteSpace: "nowrap" }}>{e.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const btnZ = {
  background: C.arena, border: `1px solid ${C.arenaDark}`, borderRadius: 6,
  padding: "6px 9px", fontSize: 12, cursor: "pointer", color: C.tierra, fontFamily: "monospace", flexShrink: 0,
};
